import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search,
  ChevronDown,
  ChevronUp,
  Tag,
  Sparkles,
  Globe,
  Database,
  Code2,
  FileText,
  Package,
  Check,
  ShoppingCart
} from 'lucide-react';
import { AgentTemplate, AgentTemplateCategory } from '../../types';
import apiService from '../../services/api';
import CustomSelect from '../common/CustomSelect';

interface AgentTemplateSelectorProps {
  onSelectTemplate: (template: AgentTemplate) => void;
  onClose: () => void;
  projectId?: string;
}

const categoryIcons: Record<AgentTemplateCategory, React.ReactNode> = {
  web_scraping: <Globe className="w-5 h-5" />,
  data_analysis: <Database className="w-5 h-5" />,
  api_integration: <Code2 className="w-5 h-5" />,
  content_generation: <FileText className="w-5 h-5" />,
  domain_research: <Search className="w-5 h-5" />,
  domain_purchase: <ShoppingCart className="w-5 h-5" />,
  general: <Package className="w-5 h-5" />
};

const categoryColors: Record<AgentTemplateCategory, string> = {
  web_scraping: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200',
  data_analysis: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 border-purple-200',
  api_integration: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 border-green-200',
  content_generation: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300 border-orange-200',
  domain_research: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300 border-indigo-200',
  domain_purchase: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300 border-teal-200',
  general: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-700'
};

const categoryDisplayNames: Record<AgentTemplateCategory, string> = {
  web_scraping: 'Web Scraping',
  data_analysis: 'Data Analysis',
  api_integration: 'API Integration',
  content_generation: 'Content Generation',
  domain_research: 'Domain Research',
  domain_purchase: 'Domain Purchase',
  general: 'General'
};

export const AgentTemplateSelector: React.FC<AgentTemplateSelectorProps> = ({
  onSelectTemplate,
  onClose,
  projectId
}) => {
  const [templates, setTemplates] = useState<AgentTemplate[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<AgentTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<AgentTemplateCategory | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedTemplates, setExpandedTemplates] = useState<Set<number>>(new Set());
  const [selectedTemplate, setSelectedTemplate] = useState<AgentTemplate | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [error, setError] = useState<string | null>(null);
  const [godaddyConfigured, setGodaddyConfigured] = useState(false);

  const godaddyCategories: AgentTemplateCategory[] = ['domain_research', 'domain_purchase'];

  // Check GoDaddy configuration for the project
  useEffect(() => {
    if (projectId) {
      apiService.getGoDaddyCredentialsStatus(parseInt(projectId, 10))
        .then(status => setGodaddyConfigured(status.configured))
        .catch(() => setGodaddyConfigured(false));
    }
  }, [projectId]);

  // Load templates on mount
  useEffect(() => {
    loadTemplates();
  }, []);

  // Filter templates when search, category, or godaddy status changes
  useEffect(() => {
    filterTemplates();
  }, [searchQuery, selectedCategory, templates, godaddyConfigured]);

  const loadTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getAgentTemplates();
      setTemplates(response.templates.filter(t => t.is_active));
      setFilteredTemplates(response.templates.filter(t => t.is_active));
    } catch (err) {
      console.error('Failed to load agent templates:', err);
      setError('Failed to load templates. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filterTemplates = useCallback(() => {
    let filtered = [...templates];

    // Hide GoDaddy templates when not configured
    if (!godaddyConfigured) {
      filtered = filtered.filter(t => !godaddyCategories.includes(t.category));
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(t => t.category === selectedCategory);
    }

    // Filter by search query (search in name, description, and tags)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t => {
        const matchName = t.name.toLowerCase().includes(query);
        const matchDescription = t.description?.toLowerCase().includes(query);
        const matchTags = t.tags?.some(tag => tag.toLowerCase().includes(query));
        return matchName || matchDescription || matchTags;
      });
    }

    setFilteredTemplates(filtered);
  }, [templates, selectedCategory, searchQuery]);

  const toggleExpanded = (templateId: number) => {
    setExpandedTemplates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(templateId)) {
        newSet.delete(templateId);
      } else {
        newSet.add(templateId);
      }
      return newSet;
    });
  };

  const handleSelectTemplate = async (template: AgentTemplate) => {
    setSelectedTemplate(template);
    try {
      // Fetch full template details if needed
      const fullTemplate = await apiService.getAgentTemplate(template.template_id);
      onSelectTemplate(fullTemplate);
    } catch (err) {
      console.error('Failed to fetch template details:', err);
      // Use the template we already have
      onSelectTemplate(template);
    }
  };

  // Get unique categories from templates (exclude GoDaddy categories when not configured)
  const availableCategories = useMemo(() => {
    let cats = templates.map(t => t.category);
    if (!godaddyConfigured) {
      cats = cats.filter(c => !godaddyCategories.includes(c));
    }
    const categories = new Set(cats);
    return Array.from(categories).sort();
  }, [templates, godaddyConfigured]);

  const TemplateCard: React.FC<{ template: AgentTemplate }> = ({ template }) => {
    const isExpanded = expandedTemplates.has(template.template_id);
    const isSelected = selectedTemplate?.template_id === template.template_id;

    return (
      <div
        data-tutorial={`template-card-${template.name.toLowerCase().replace(/\s+/g, '-')}`}
        className={`border rounded-lg p-4 transition-all duration-200 hover:shadow-lg cursor-pointer ${
          isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:border-gray-600'
        }`}
      >
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            {categoryIcons[template.category]}
            <h3 className="font-semibold text-gray-900 dark:text-warm-white">{template.name}</h3>
          </div>
          {isSelected && <Check className="w-5 h-5 text-primary-500" />}
        </div>

        <div className="flex items-center gap-2 mb-3">
          <span
            className={`px-2 py-1 text-xs font-medium rounded-md border ${
              categoryColors[template.category]
            }`}
          >
            {categoryDisplayNames[template.category]}
          </span>
          {template.tags?.slice(0, 2).map((tag, idx) => (
            <span key={idx} className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
              <Tag className="w-3 h-3" />
              {tag}
            </span>
          ))}
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
          {template.description || 'No description available'}
        </p>

        <div className="flex items-center justify-between">
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleExpanded(template.template_id);
            }}
            className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="w-4 h-4" />
                Hide Details
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                View Details
              </>
            )}
          </button>
          <button
            onClick={() => handleSelectTemplate(template)}
            className="px-3 py-1 bg-primary-600 text-white rounded-md hover:bg-primary-700 text-sm transition-colors"
          >
            Use Template
          </button>
        </div>

        {isExpanded && (
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 space-y-3">
            {/* Model Information */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Model</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">{template.model}</p>
            </div>

            {/* Tools */}
            {template.tool_configurations && template.tool_configurations.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Configured Tools</h4>
                <div className="flex flex-wrap gap-1">
                  {template.tool_configurations.map((tool, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded-md"
                    >
                      {tool.name || tool.type}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Sample Inputs */}
            {template.sample_inputs && template.sample_inputs.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Example Inputs</h4>
                <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 space-y-1">
                  {template.sample_inputs.slice(0, 3).map((input, idx) => (
                    <li key={idx}>{input}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Sample Outputs */}
            {template.sample_outputs && template.sample_outputs.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Example Outputs</h4>
                <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 space-y-1">
                  {template.sample_outputs.slice(0, 3).map((output, idx) => (
                    <li key={idx}>{output}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Instructions Preview */}
            {template.instructions && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Instructions Preview</h4>
                <pre className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap font-sans max-h-48 overflow-y-auto bg-gray-50 dark:bg-gray-800 rounded-md p-3 border border-gray-200 dark:border-gray-700">{template.instructions}</pre>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div data-tutorial="modal-template-selector" className="bg-white dark:bg-cap-dark rounded-lg w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-primary-600" />
              <h2 className="text-xl font-semibold">Choose an Agent Template</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-400 transition-colors"
            >
              ×
            </button>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search templates by name, description, or tags..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            {/* Category Filter */}
            <CustomSelect
              value={selectedCategory}
              onChange={(value) => setSelectedCategory(value as AgentTemplateCategory | 'all')}
              options={[
                { value: 'all', label: 'All Categories' },
                ...availableCategories.map((cat) => ({
                  value: cat,
                  label: categoryDisplayNames[cat as AgentTemplateCategory],
                })),
              ]}
              placeholder="All Categories"
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            />

            {/* View Mode Toggle */}
            <div className="flex gap-1 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3 py-1 rounded ${
                  viewMode === 'grid'
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                Grid
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-1 rounded ${
                  viewMode === 'list'
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                List
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {error ? (
            <div className="text-center py-12">
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={loadTemplates}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
              >
                Try Again
              </button>
            </div>
          ) : loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              <p className="mt-4 text-gray-600 dark:text-gray-400">Loading templates...</p>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 dark:text-gray-400">
                {searchQuery || selectedCategory !== 'all'
                  ? 'No templates found matching your filters.'
                  : 'No templates available.'}
              </p>
            </div>
          ) : (
            <div
              className={
                viewMode === 'grid'
                  ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'
                  : 'space-y-4'
              }
            >
              {filteredTemplates.map((template) => (
                <TemplateCard key={template.template_id} template={template} />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''} available
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 transition-colors"
            >
              Cancel
            </button>
            {selectedTemplate && (
              <button
                onClick={() => handleSelectTemplate(selectedTemplate)}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
              >
                Continue with {selectedTemplate.name}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};